"use client";

import { dataLayer } from "@repo/analytics/data-layer-utils";
import { componentNameConstants, dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import {
  selectIsAuthSuccessOrPerpetual,
  setIsCommonSsoModalVisible,
  store,
  useAppSelector,
} from "@repo/web-store";
import React, { FC, memo, useCallback, useMemo, useRef, useState } from "react";
import NoResultsFound from "../../../components/NoResultsFound/NoResultsFound";
import StockTableRow from "../../../components/StockListingTable/StockTableRow";
import WatchlistModal from "../../../components/Watchlist/WatchlistModal";
import { IStockListingRow } from "../../../interfaces/StockListingTable.interface";
import type { StockListingUILabels } from "../../../interfaces/StockListingTableAEM.interface";

export interface StockTableProps {
  /** Stock data rows */
  data: IStockListingRow[];
  /** Entity type identifier - from AEM, can be null */
  entityType: string | null;
  /** Whether to show skeleton loading state */
  isLoading?: boolean;
  /** When true and rows exist, dims the table while a filtered list is being refetched */
  isListUpdating?: boolean;
  /** Whether to show a top border on the table header row */
  showHeaderTopBorder?: boolean;
  /** AEM table column labels */
  tableLabels?: {
    companyName?: string;
    ltp?: string;
  };
  /** AEM UI labels */
  uiLabels?: StockListingUILabels;
  /** AEM page title — used as EP_PAGE_NAME in CT stock-click events */
  pageTitle?: string | null;
  /** Watchlist modal props */
  watchlistProps?: {
    modalTitle?: string;
    modalBtnCta?: string;
    successMsg?: string;
    errorMsg?: string;
    cta_text?: string;
    section_title?: string;
  };
  /** Controls bookmark visibility in each row */
  showBookmark?: boolean;
  /** Applies Global Indices specific wrapper spacing */
  isGlobalIndicesPage?: boolean;
}

/**
 * Table component for displaying stock listings.
 * Handles desktop and mobile views, row clicks, and watchlist interactions.
 */
const StockTable: FC<StockTableProps> = memo(
  ({
    data,
    entityType,
    isLoading = false,
    isListUpdating = false,
    showHeaderTopBorder = false,
    tableLabels,
    uiLabels = {},
    pageTitle,
    watchlistProps,
    showBookmark = true,
    isGlobalIndicesPage = false,
  }) => {
    const loginStatus = useAppSelector(selectIsAuthSuccessOrPerpetual);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStock, setSelectedStock] = useState<{
      exchange: number;
      token: number;
      symbol: string;
      fullName: string;
    } | null>(null);
    const bookmarkClickRef = useRef<boolean>(false);

    const hasData = Array.isArray(data) && data.length > 0;
    const showEmpty = !isLoading && !hasData;

    const rows: IStockListingRow[] = useMemo(() => {
      if (hasData) return data;
      return Array.from({ length: 10 }).map((_, index) => ({
        id: `skeleton-${index}`,
      }));
    }, [data, hasData]);

    const rowClasses = useMemo(
      () => ({
        tdPaddingClass: "py-4",
        tdRightClass: "py-4 text-right",
        symbolTextClass: "text-[14px] md:text-base",
        nameTextClass: "text-[10px] md:text-sm",
        nameTextClassGlobalIndices: "text-[10px] md:text-[12px]",
        ltpTextClass: "text-[14px] md:text-base",
        changeTextClass: "text-[10px] md:text-sm",
      }),
      [],
    );

    const buildRowKey = useCallback(
      (item: IStockListingRow, index: number) => {
        const prefix = item.symbol ?? item.id ?? entityType ?? "stock";
        return `${prefix}-${index}`;
      },
      [entityType],
    );

    const handleBookmarkClick = useCallback(
      (e: React.MouseEvent, item: IStockListingRow) => {
        e.preventDefault();
        e.stopPropagation();
        bookmarkClickRef.current = true;

        const analyticsData = {
          section_title: watchlistProps?.section_title ?? (uiLabels.stockListingAriaLabel ?? "Stock Listing"),
          component: watchlistProps?.section_title ?? (uiLabels.stockListingAriaLabel ?? "Stock Listing"),
          event: dlEventConstants.CLICK,
          element: dlElementConstants.WATCH_LIST_ICON_CLICK,
          cta_text: watchlistProps?.cta_text ?? (uiLabels.addToWatchlistAriaLabel ?? "Add to Watchlist"),
          event_type: dlEventConstants.ICON_CLICK,
        };

        const cleverTapObj = {
          EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
          EP_TILE_CLICKED: item.fullName ?? "",
          EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
        };
        dataLayer("", analyticsData, cleverTapObj);

        if (!loginStatus) {
          store.dispatch(setIsCommonSsoModalVisible(true));
          setTimeout(() => {
            bookmarkClickRef.current = false;
          }, 500);
          return;
        }

        if (item.exchange !== undefined && item.token !== undefined && item.symbol && item.fullName) {
          setSelectedStock({
            exchange: item.exchange,
            token: item.token,
            symbol: item.symbol,
            fullName: item.fullName,
          });
          setIsModalOpen(true);
        }
      },
      [loginStatus, watchlistProps],
    );

    const handleCloseModal = useCallback(() => {
      setIsModalOpen(false);
      setSelectedStock(null);
    }, []);

    const handleRowClick = useCallback(() => {
      if (bookmarkClickRef.current) {
        bookmarkClickRef.current = false;
      }
    }, []);

    const handleLinkClick = useCallback((e: React.MouseEvent) => {
      if (bookmarkClickRef.current) {
        e.preventDefault();
        e.stopPropagation();
        bookmarkClickRef.current = false;
        return false;
      }
    }, []);

    const handleBookmarkMouseDown = useCallback(() => {
      bookmarkClickRef.current = true;
    }, []);

    const handleBookmarkMouseUp = useCallback(() => {
      setTimeout(() => {
        bookmarkClickRef.current = false;
      }, 100);
    }, []);

    const handleStockClick = useCallback(
      (stockName: string) => {
        const dlData = {
          component: componentNameConstants.STOCK_LIST_CARD,
          event: dlEventConstants.CLICK,
          event_type: dlEventConstants.BUTTON_CLICK,
          element: dlElementConstants.PLP_CLICKED,
          cta_text: stockName,
        };
        const cleverTapObj = {
          EP_STOCK_NAME: stockName,
          EP_PAGE_NAME: pageTitle ?? "",
          EP_CTA: stockName,
          EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_STOCK_PLP,
        };
        dataLayer("", dlData, cleverTapObj);
      },
      [pageTitle],
    );

    // Keep the list layout stable during refetch:
    // - avoid opacity/blur effects that users perceive as "blinking"
    // - show a loader overlay instead
    const updatingClass = isListUpdating ? "pointer-events-none" : "";
    const visibleRows = rows;

    return (
      <div
        className={`relative ${updatingClass}`}
        aria-busy={isListUpdating || undefined}
        aria-live={isListUpdating ? "polite" : undefined}
      >

        <div className="w-full px-4 py-1">
          <table className="w-full max-w-[868px] border-collapse text-sm md:text-base">
            {!isGlobalIndicesPage && <thead className="hidden md:table-header-group">
              <tr className="box-border h-[41.5px] w-full max-w-[868px] border-b border-[rgb(229,229,229)] py-3 leading-[19.2px] text-[rgb(26,26,26)]">
                <th className="w-[60%] text-left text-sm font-normal">{tableLabels?.companyName ?? "Company name"}</th>
                <th className="w-[40%] text-right text-sm font-normal">{tableLabels?.ltp ?? "LTP"}</th>
              </tr>
            </thead>}

            <tbody>
              {showEmpty ? (
                <tr>
                  <td className="px-0 py-6 md:py-10" colSpan={2}>
                    <NoResultsFound
                      title={uiLabels.noResultsTitle}
                      description={uiLabels.noResultsDescription}
                      aemImages={
                        uiLabels.noResultsImageSrc
                          ? { noResultsImage: uiLabels.noResultsImageSrc }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              ) : (
                visibleRows.map((item, index) => {
                  const key = buildRowKey(item, index);
                  return (
                    <StockTableRow
                      key={key}
                      item={item}
                      rowKey={key}
                      isSkeleton={isListUpdating}
                      classes={rowClasses}
                      showBookmark={showBookmark}
                      uiLabels={uiLabels}
                      onStockClick={handleStockClick}
                      onRowClick={handleRowClick}
                      onLinkClick={handleLinkClick}
                      onBookmarkClick={handleBookmarkClick}
                      onBookmarkMouseDown={handleBookmarkMouseDown}
                      onBookmarkMouseUp={handleBookmarkMouseUp}
                      isGlobalIndicesPage={isGlobalIndicesPage}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {selectedStock && watchlistProps?.modalTitle && (
          <WatchlistModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            selectedStock={selectedStock}
            successMsg={watchlistProps.successMsg}
            errorMsg={watchlistProps.errorMsg}
            modalTitle={watchlistProps.modalTitle}
            modalBtnCta={watchlistProps.modalBtnCta}
          />
        )}
      </div>
    );
  },
);

StockTable.displayName = "StockTable";

export default StockTable;
